import { Card, CardContent, CardHeader, Grid } from '@mui/material';
import { BarTooltipProps, ResponsiveBar } from '@nivo/bar';
import { PropsWithChildren } from 'react';

import { LikertData, LikertLegendData } from '../types';

import { useSxStyles } from './useStyles';

type Props = {
  data: LikertData[];
  legendData: LikertLegendData[];
  borderColor?: string;
  title: string;
  total: number;
};

export const LikertChart: React.FC<Props> = ({
  data,
  title,
  legendData,
  total,
}) => {
  const sxStyles = useSxStyles();

  const getLabelFormat = (v: number) =>
    `${((v * 100) / total).toFixed(1).toString()}%`;

  const ColorBox = ({ label, color }: { label: string; color: string }) => {
    const colorBoxStyle = {
      background: `${color}`,
      height: '18px',
      marginRight: '8px',
      minWidth: '18px',
      width: '18px',
    };

    return (
      <Grid item sx={sxStyles.colorBox}>
        <span style={colorBoxStyle} /> {label}
      </Grid>
    );
  };

  const getCustomTooltip = (
    d: PropsWithChildren<BarTooltipProps<LikertData>>,
    _e: React.MouseEvent<HTMLCanvasElement>,
  ) => (
    <ColorBox
      label={`${d.data[`${d.id}Label` as keyof LikertData]}: ${d.value}`}
      color={d.color}
    />
  );

  const renderLegend = (lData: LikertLegendData[]) =>
    lData.map((d) => (
      <ColorBox label={d.label} color={d.color} key={d.color} />
    ));

  return (
    <Card sx={sxStyles.chartCard}>
      <CardHeader
        sx={sxStyles.chartHeader}
        title={title}
        titleTypographyProps={{ sx: sxStyles.chartCardTitle }}
      />
      <CardContent sx={sxStyles.chartCardContent}>
        <Grid container sx={sxStyles.likertChartContainer}>
          <ResponsiveBar
            data={data}
            keys={['c1', 'c2', 'c3', 'c4', 'c5']}
            indexBy="question"
            margin={{
              top: 0,
              right: 10,
              bottom: 60,
              left: 10,
            }}
            padding={0.5}
            innerPadding={1}
            layout="horizontal"
            colors={({ id, data }) =>
              `${data[`${id}Color` as keyof LikertData]}`
            }
            axisTop={null}
            axisRight={null}
            axisBottom={null}
            axisLeft={null}
            enableGridY={false}
            tooltip={(d, e) => getCustomTooltip(d, e)}
            labelSkipWidth={15}
            labelSkipHeight={22}
            labelTextColor="#000"
            label={(d) => getLabelFormat(d.value || 0)}
            legends={[]}
          />
          <Grid
            container
            item
            sx={sxStyles.likertLegend}
            direction="row"
            justifyContent="space-between"
            wrap="wrap"
            spacing={8}
          >
            {renderLegend(legendData)}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
